import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getShapeChromeScreenRect,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'
import { POPOVER_GAP, POPOVER_WIDTH } from '../constants/popover-layout'

/** Keeps a popover of `height` inside the floating-chrome boundaries, 8px from each edge. */
export function clampPos(x: number, y: number, height: number): { x: number; y: number } {
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()
  const leftBoundary = getLeftBoundary()
  const rightBoundary = getRightBoundary()

  return {
    x: Math.max(leftBoundary + 8, Math.min(rightBoundary - POPOVER_WIDTH - 8, x)),
    y: Math.max(topBoundary + 8, Math.min(bottomBoundary - height - 8, y)),
  }
}

/** Anchors the popover beside the photo's chrome: to the right if it fits, else above, else below. */
export function computePopoverPos(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect,
  popoverHeight: number
): { x: number; y: number } {
  const {
    top: st,
    bottom: sb,
    left: sl,
    right: sr,
  } = getShapeChromeScreenRect(shape, scale, canvasRect)
  const shapeCenterX = (sl + sr) / 2

  const rightX = sr + POPOVER_GAP

  if (rightX + POPOVER_WIDTH <= getRightBoundary() - 8) {
    return clampPos(rightX, st, popoverHeight)
  }

  const aboveY = st - popoverHeight - POPOVER_GAP

  if (aboveY >= getTopBoundary() + 8) {
    return clampPos(shapeCenterX - POPOVER_WIDTH / 2, aboveY, popoverHeight)
  }

  return clampPos(shapeCenterX - POPOVER_WIDTH / 2, sb + POPOVER_GAP, popoverHeight)
}
